import React, { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useBlogCategory } from "../useHook";
import Swal from "sweetalert2";
import AddBlogCategoryModal from "../../../Components/Modals/BlogVategoryModal/BlogCategoryModal";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";

function BlogCategories() {
  const [modalOpen, setModalOpen] = useState(false);
  const [blogCategories, setBlogCategories] = useState([]);
  const [currentBlogCategory, setCurrentBlogCategory] = useState(null);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [nameQuery, setNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const {
    addBlogCategories,
    getBlogCategories,
    updateBlogCategory,
    updateCategoryStatus,
    searchBlogCategories,
  } = useBlogCategory();

  const navigate = useNavigate();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  useEffect(() => {
    const fetchBlogCategories = async () => {
      setIsLoading(true);
      try {
        await getBlogCategories(setBlogCategories, setTotal, offset, limit, nameQuery, statusFilter);
      } catch (error) {
        console.error("Error fetching blog categories:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load blog categories. Please refresh the page.",
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchBlogCategories();
  }, [offset]);

  const handleSaveCategory = async (categoryData, id = null) => {
    try {
      let response;
      if (id) {
        response = await updateBlogCategory(id, categoryData);
      } else {
        response = await addBlogCategories(categoryData);
      }

      if (response) {
        await getBlogCategories(setBlogCategories, setTotal, offset, limit, nameQuery, statusFilter);
        toggleModal();
        Swal.fire("Success", "Category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    }
  };

  const handleEditCategory = (category) => {
    setCurrentBlogCategory(category);
    toggleModal();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      const response = await updateCategoryStatus(id, newStatus);

      if (response) {
        setBlogCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === id ? { ...category, status: newStatus } : category
          )
        );

        Swal.fire({
          title: "Success",
          text: `Category status has been ${newStatus ? "activated" : "deactivated"
            } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update category status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    }
  };

  const handleViewPage = (id) => {
    navigate("/blog-category/view", { state: { id } });
  };

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchBlogCategories(nameQuery, statusFilter, 0, limit);
      setBlogCategories(response.data);
      const totalRecords = response.pagination?.totalCount || response.data.length;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setNameQuery("");
    setStatusFilter("");
    setOffset(0);
    getBlogCategories(setBlogCategories, setTotal, 0, limit, "", "");
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading blog categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-100 p-4 flex justify-between">
        <h2 className="text-2xl font-bold">Blog Categories</h2>
        <button
          className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          onClick={() => {
            setCurrentBlogCategory(null);
            toggleModal();
          }}
        >
          Add Blog Category
        </button>
      </div>

      <div className="mt-4">
        <div className="bg-white p-4 rounded-md shadow-md mb-6 flex gap-4 mt-4">
          <input
            type="text"
            className="w-[40%] flex-grow px-4 py-2 border rounded-lg"
            placeholder="Search by category name"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
          />
          <select
            className="w-[20%] px-4 py-2 border rounded-lg"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="" className="opacity-25">
              Select Status
            </option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-blue-500 text-white px-4 py-2 rounded-md"
            onClick={handleSearch}
          >
            Search
          </button>
          <button
            className="w-full sm:w-[45%] md:w-60 bg-primary-dark text-white px-4 py-2 rounded-md"
            onClick={handleReset}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="overflow-x-auto mt-6">
        <table className="min-w-full table-auto bg-white shadow-md rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gradient-to-r from-primary-dark to-primary-light text-white">
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Sr. No.
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Category Name
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Status
              </th>
              <th className="px-6 py-3 text-left text-sm font-semibold">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(blogCategories) && blogCategories?.length > 0 ? (
              blogCategories?.map((category, index) => (
                <tr
                  key={category.id}
                  className={`${index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                >
                  <td className="px-4 py-2">{offset + index + 1}</td>
                  <td className="px-4 py-2">{category.name}</td>
                  <td className="px-6 py-4">
                    <div
                      className={`relative inline-block w-16 h-8 rounded-full transition-colors duration-300 cursor-pointer ${category.status ? "bg-primary-dark" : "bg-[#cccccc]"
                        }`}
                      onClick={() =>
                        handleToggleStatus(category.id, category.status)
                      }
                    >
                      <div
                        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${category.status ? "translate-x-8" : ""
                          }`}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 flex space-x-4">
                    <button
                      className="bg-primary-dark text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                      onClick={() => handleEditCategory(category)}
                    >
                      <MdEdit />
                    </button>
                    <button
                      className="bg-blue-500 text-white py-2 px-4 rounded-lg shadow-md flex items-center"
                      onClick={() => handleViewPage(category.id)}
                    >
                      <IoEye />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center py-6 text-gray-500">
                  No blog categories found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Component */}
      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="mt-4"
          />
        </div>
      )}

      <AddBlogCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentBlogCategory}
        handleSaveCategory={handleSaveCategory}
      />
    </div>
  );
}

export default BlogCategories;
